import '../../styles.scss';

import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Alert, Box, Button, Flex, Text, Title } from '@gjensidige/builders-components';

import { postEquinorAccident } from '../../api';
import { Dropdown, Input, InputGroup } from '../../components';
import { accidentRelationOptions, occupationalInjuryEquinor } from '../../constants';
import { Link } from '../../utils/link';
import { EquinorAccident, equinorAccidentForm } from '../../utils/types';
import HeaderEquinorForms from './components/headerEquinorForms/HeaderEquinorForms';

const EquinorAccidentForm = () => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EquinorAccident>({
    resolver: zodResolver(equinorAccidentForm),
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [errorSubmitting, setErrorSubmitting] = useState<boolean>(false);

  const onSubmit: SubmitHandler<EquinorAccident> = async (data) => {
    setIsSubmitting(true);
    setErrorSubmitting(false);
    postEquinorAccident(data)
      .then((response) => {
        if (response === 'sent') {
          setIsSubmitting(false);
          setSuccess(true);
        }
      })
      .catch(() => {
        setIsSubmitting(false);
        setErrorSubmitting(true);
      });
  };

  return (
    <form
      method="post"
      onSubmit={(e) => {
        e.preventDefault();
        setIsSubmitting(true);
        handleSubmit(onSubmit);
      }}
    >
      <Flex layout={{ xs: 1, md: 4.8 }} className="schema">
        <div>
          <HeaderEquinorForms
            title="Accident / Illness"
            description="To fillout form regarding death"
            url={Link.getEquinorDeath()}
          />
        </div>
        <div>
          <Box variant="form-section">
            <Title as="h2" size="4">
              Information about the insured person
            </Title>
            <Text className="builders-margin-bottom-60">
              (to be filled in to the best of your knowledge)
            </Text>
            <Input
              name="f01_name"
              control={control}
              labelText="Full name *"
              error={errors.f01_name}
              tracking={false}
            />
            <Input
              name="f02_dateOfBirth"
              control={control}
              labelText="Date of birth *"
              placeholder="DD/MM/YYYY"
              error={errors.f02_dateOfBirth}
              tracking={false}
            />
            <Input
              name="f03_address"
              control={control}
              labelText="Address *"
              error={errors.f03_address}
              tracking={false}
            />
            <Input
              name="f04_postalCode"
              control={control}
              labelText="Post code *"
              error={errors.f04_postalCode}
              tracking={false}
            />
            <Input
              name="f05_city"
              control={control}
              labelText="City *"
              error={errors.f05_city}
              tracking={false}
            />
            <Input
              name="f06_position"
              control={control}
              labelText="Position/profession *"
              error={errors.f06_position}
              tracking={false}
            />
            <Input
              name="f07_dateEmployment"
              control={control}
              labelText="Date of employment *"
              placeholder="DD/MM/YYYY"
              error={errors.f07_dateEmployment}
              tracking={false}
            />
            <Input
              name="f08_email"
              control={control}
              labelText="Email *"
              error={errors.f08_email}
              tracking={false}
            />
            <Input
              name="f09_phoneNumber"
              control={control}
              labelText="Phone number"
              error={errors.f09_phoneNumber}
              tracking={false}
            />
            <Input
              name="f10_bankAccount"
              control={control}
              labelText="Bank account number *"
              error={errors.f10_bankAccount}
              tracking={false}
            />
            <Input
              name="f11_dateOfInjury"
              control={control}
              labelText="Date of injury / sick leave *"
              placeholder="DD/MM/YYYY"
              error={errors.f11_dateOfInjury}
              tracking={false}
            />
            <InputGroup
              name="f12_relation"
              control={control}
              labelText="Your relation to Equinor: *"
              options={accidentRelationOptions}
              type="radio"
              variant="column"
              error={errors.f12_relation}
              tracking={false}
            />
            <Dropdown
              name="f13_object"
              control={control}
              labelText="The claim form is for: *"
              placeholder="Choose..."
              options={occupationalInjuryEquinor}
              error={errors.f13_object}
              tracking={false}
            />
            <Input
              name="f14_spouse"
              control={control}
              labelText="If the claim form is for co-insured spouse/cohabitant or child of expat, please fill in their name and date of birth:"
              error={errors.f14_spouse}
              tracking={false}
            />
            <Input
              name="f15_relation"
              control={control}
              labelText="If the claim is not reported by the employee her-/himself, in which capacity/which relation, as a reporter of the claim, do you have to the employee?"
              error={errors.f15_relation}
              tracking={false}
            />
          </Box>
          <Button
            variant="critical"
            type="submit"
            className={'submit-button'}
            loading={isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            Submit
          </Button>
          {success && (
            <Alert severity="success" className="sending-alert">
              <Text>Form sent</Text>
            </Alert>
          )}
          {errorSubmitting && (
            <Alert severity="error" className="sending-alert">
              <Text>Failed to send form, try again later</Text>
            </Alert>
          )}
        </div>
      </Flex>
    </form>
  );
};

export default EquinorAccidentForm;
